import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { PiBuildingOfficeBold, PiStudent } from "react-icons/pi";
import { callApi, CDN_URL, formatDate, formatMoney, formatMoneyShorten, getInlineLoader, setCookie } from "../Helpers";
import { FaChalkboardTeacher } from "react-icons/fa";
import { SiGoogleclassroom } from "react-icons/si";
import { IoIosPeople } from "react-icons/io";
import { MdSearch } from "react-icons/md";
import { FiCheckCircle, FiClock, FiArrowRight, FiEyeOff, FiFileText, FiMail } from "react-icons/fi";

export default function View({ uuid }) {
  const appContext = useContext(AppContext);

  /* Place states here */
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  
  const [isApplying, setIsApplying] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [showOtpPrompt, setShowOtpPrompt] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [otpInput, setOtpInput] = useState("");

  async function init() {
    // run initializations here
    setReady(false);
    await Promise.all([
      appContext.authCheck(),
      (async () => {
        if (uuid) {
          await getData();
        }
      })(),
    ]);
    setReady(true);
  }

  async function getData() {
    await callApi('jobapplicant/get-job-data', { jobUuid: uuid }).then((response) => {
      if (response && response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError('Could not fetch data');
      }
    });
  }

  async function handleApplyInitiation() {
    setIsApplying(true);
    const isAuthenticated = appContext.auth;
    setIsApplying(false);

    if (isAuthenticated) {
      sendApplication();
    } else {
      setShowEmailPrompt(true);
      setShowOtpPrompt(false);
    }
  }

  async function handleEmailSubmit() {
    if (!emailInput || !emailInput.includes('@')) {
      appContext.tellError("Please enter a valid email address.");
      return;
    }

    setIsApplying(true);
    await callApi('jobapplicant/access-account', { email: emailInput }).then(response => {
      if (response && response.status === 1) {
        appContext.tellMessage(response.msg);
        setShowOtpPrompt(true); 
      } else {
        appContext.tellError(response.msg || "Failed to process request.");
      }
    }).catch(() => {
      appContext.tellError("Network error occurred.");
    });
    setIsApplying(false);
  }

  async function handleOtpSubmit() {
    if (!otpInput) {
      appContext.tellError("Please enter the OTP.");
      return;
    }

    setIsApplying(true);
    await callApi('jobapplicant/verify-otp', { email: emailInput, otp: otpInput }).then(response => {
      if (response && response.status === 1) {
        appContext.tellMessage(response.msg);
        setCookie('userUuid', response.data.uuid, 30);
        setCookie('userToken', response.data.token, 30);
        resetPrompts();
        sendApplication();
      } else {
        appContext.tellError(response.msg || "Invalid OTP.");
      }
    }).catch(() => {
      appContext.tellError("Network error occurred.");
    });
    setIsApplying(false);
  }

  function resetPrompts() {
    setShowEmailPrompt(false);
    setShowOtpPrompt(false);
    setEmailInput("");
    setOtpInput("");
  }

  async function sendApplication() {
    setIsApplying(true);

    await callApi('jobapplicant/get-current-applicant-data', {}).then((response) => {
      if (response && response.status === 1) {
        const applicant = response.data;
        setProfile(applicant);

        // Evaluate raw properties immediately to avoid state delay lag
        const isIncomplete = 
          !applicant.firstName?.trim() ||
          !applicant.middleName?.trim() ||
          !applicant.lastName?.trim() ||
          !applicant.phone?.trim() ||
          !applicant.physicalAddress?.trim() ||
          !applicant.dob?.trim() ||
          !applicant.gender?.trim();

        if (isIncomplete) {
          appContext.tellError("Please complete your profile details before submitting an application.");
          appContext.navTo({
            item: 'side-view',
            subItem: 'edit-applicant-profile'
          });
        } else {
          appContext.navTo({
            item: 'side-view',
            subItem: 'create-application', 
            extraItem: data.uuid
          });
        }
      } else {
        appContext.tellError(response.msg || "Could not retrieve verification profile.");
      }
    }).catch(() => {
      appContext.tellError("Network connection error verification loop.");
    });

    setIsApplying(false);
  }

  useEffect(() => {
    init();
  }, [uuid]);

  useEffect(() => {
    appContext.setShowOverlayLoader(loading);
    return () => {
      appContext.setShowOverlayLoader(false);
    }
  }, [loading]);

  if (ready && data) {
    return (
      <div className="container">
        <div className="row">
          
          <div className="col-12 mt-3 mb-3">
            <div className="jd-section">
              <div className="jd-container">
                
                {/* Header Area */}
                <div className="jd-header">
                  <h1 className="jd-title">{data.title}</h1>
                  <div>
                    <span className={`jd-status-badge ${data.status === 'published' ? 'status-published' : data.status === 'unpublished' ? 'status-unpublished' : 'status-default'}`}>
                      {data.status === 'published' ? <FiCheckCircle size={16} /> : <FiClock size={16} />}
                      Status: {data.status}
                    </span>
                  </div>
                </div>

                {/* Description Body Area */}
                <div className="jd-card">
                  <h4 className="jd-card-title">
                    <FiFileText color="var(--clinical-teal)" />
                    Job Description
                  </h4>
                  <div 
                    className="jd-content"
                    dangerouslySetInnerHTML={{ __html: data.description }}
                  ></div>
                </div>

                {/* Actions Bar Area */}
                <div className="jd-actions">
                  
                  {!showEmailPrompt && !showOtpPrompt ? (
                    <button
                      className="jd-btn jd-btn-primary"
                      onClick={handleApplyInitiation}
                      disabled={isApplying}
                    >
                      {isApplying ? "Checking details..." : "Apply Now"} {!isApplying && <FiArrowRight />}
                    </button>
                  ) : showOtpPrompt ? (
                    <div className="jd-email-prompt">
                      <div style={{ fontSize: '0.9rem', color: 'var(--clinical-teal)', fontWeight: 600 }}>
                        We sent a code to {emailInput}
                      </div>
                      <input 
                        type="text"
                        className="jd-email-input"
                        placeholder="Enter the 6-digit OTP..."
                        value={otpInput}
                        onChange={(e) => setOtpInput(e.target.value)}
                        autoFocus
                      />
                      <div className="jd-btn-group">
                        <button 
                          className="jd-btn jd-btn-primary"
                          onClick={handleOtpSubmit}
                          disabled={isApplying}
                        >
                          {isApplying ? "Verifying..." : "Verify OTP"} {!isApplying && <FiArrowRight />}
                        </button>
                        <button 
                          className="jd-btn jd-btn-danger"
                          onClick={resetPrompts}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="jd-email-prompt">
                      <input 
                        type="email"
                        className="jd-email-input"
                        placeholder="Enter your email to proceed..."
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        autoFocus
                      />
                      <div className="jd-btn-group">
                        <button 
                          className="jd-btn jd-btn-primary"
                          onClick={handleEmailSubmit}
                          disabled={isApplying}
                        >
                          {isApplying ? "Processing..." : "Continue"} {!isApplying && <FiArrowRight />}
                        </button>
                        <button 
                          className="jd-btn jd-btn-danger"
                          onClick={resetPrompts}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    );
  } else {
    return (
      <div className="container mSupportLoading">
        {getInlineLoader()}
      </div>
    );
  }
}